// Port of Flutter's locationDataScreen.dart (+ locationFunctions.dart).
//
// One form for both jobs, chosen by the route param:
//   /location/new         create — AddLocation then AddUserLocationMapping
//   /location/:locationId edit   — UpdateLocation on the address fields
//
// The FAB carries the Dart's LocationMenu: list the WiFi networks stored against
// this location, and add one (SSID + password), which is what devices installed
// there are provisioned from.

import React, { useEffect, useState } from 'react';
import {
  Box, TextField, Button, CircularProgress, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemIcon, ListItemText,
} from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiFindSharpIcon from '@mui/icons-material/WifiFindSharp';
import { useNavigate, useParams } from 'react-router-dom';
import { CenterTitleBar } from '../components/MobileAppBar';
import CircleMenu from '../components/CircleMenu';
import { useMessage } from '../components/MessageDialog';
import { useApp } from '../../../core/state/AppContext';
import {
  getLocationDetail, addLocation, addUserLocationMapping, updateLocation,
  getLocationWifiList, addLocationWifi,
} from '../../../core/api/api';

const FIELDS = [
  ['ADD1', 'Address Line 1'],
  ['ADD2', 'Address Line 2'],
  ['CITY', 'City'],
  ['PINCODE', 'Pincode'],
  ['Longitude', 'Longitude'],
  ['Latitude', 'Latitude'],
];

export default function LocationDataScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { locationId } = useParams();
  const { user, userId, retryLocations } = useApp();
  const { show } = useMessage();

  const isNew = locationId === 'new';
  const [form, setForm] = useState({
    LocationName: '', ADD1: '', ADD2: '', CITY: '', PINCODE: '', Longitude: '', Latitude: '',
  });
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);

  const [wifiList, setWifiList] = useState(null);
  const [addWifiOpen, setAddWifiOpen] = useState(false);
  const [ssid, setSsid] = useState('');
  const [wifiPass, setWifiPass] = useState('');

  useEffect(() => {
    if (isNew) return undefined;
    let alive = true;
    getLocationDetail(locationId)
      .then((res) => {
        const row = Array.isArray(res.data) ? res.data[0] : res.data;
        if (alive && row) {
          setForm({
            LocationName: row.LocationName ?? '',
            ADD1: row.ADD1 ?? '', ADD2: row.ADD2 ?? '', CITY: row.CITY ?? '',
            PINCODE: row.PINCODE ?? '', Longitude: row.Longitude ?? '', Latitude: row.Latitude ?? '',
          });
        }
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isNew, locationId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        if (!form.LocationName.trim()) {
          show('Invalid Data', 'Please enter a location name.');
          return;
        }
        const res = await addLocation({
          LocationName: form.LocationName.trim(),
          MasterUserId: user?.UserId,
          ADD1: form.ADD1, ADD2: form.ADD2, CITY: form.CITY,
          PINCODE: form.PINCODE, Longitude: form.Longitude, Latitude: form.Latitude,
          MOBILE1: user?.MobileNumber,
        });
        const insertId = res?.data?.insertId;
        if (insertId == null) throw new Error('no insertId');
        await addUserLocationMapping(userId, String(insertId));
      } else {
        await updateLocation(locationId, {
          ADD1: form.ADD1, ADD2: form.ADD2, CITY: form.CITY,
          PINCODE: form.PINCODE, Longitude: form.Longitude, Latitude: form.Latitude,
        });
      }
      retryLocations();
      navigate('/home?tab=1', { replace: true });
    } catch (e) {
      show("Can't Add Location",
        "The Location couldn't be added properly, contact assistance to solve your issue.");
    } finally {
      setBusy(false);
    }
  };

  const showWifi = async () => {
    const res = await getLocationWifiList(locationId).catch(() => null);
    const list = Array.isArray(res?.data) ? res.data : [];
    if (!list.length) {
      show('No WiFi', 'No WiFi networks found for this location.');
      return;
    }
    setWifiList(list);
  };

  const submitWifi = async () => {
    try {
      await addLocationWifi(locationId, ssid, wifiPass);
      setAddWifiOpen(false);
      setSsid(''); setWifiPass('');
      show('WiFi Added', 'The WiFi was added successfully to location.');
    } catch (e) {
      show('WiFi Not Added', 'Could not add the WiFi network. Please try again.');
    }
  };

  const field = (key, label, disabled = false) => (
    <TextField
      key={key}
      fullWidth
      label={label}
      value={form[key]}
      onChange={set(key)}
      disabled={disabled}
      sx={{
        my: 0.75,
        '& .MuiInputLabel-root': { color: c.text },
        '& .MuiOutlinedInput-root': {
          bgcolor: `${c.back}80`,
          borderRadius: '8px',
          color: c.text,
          '& fieldset': { borderColor: c.text },
          '&:hover fieldset': { borderColor: c.text },
          '&.Mui-focused fieldset': { borderColor: c.text, borderWidth: 2 },
        },
      }}
    />
  );

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <CenterTitleBar title={isNew ? 'Add Location' : 'Location Details'} showBack />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress sx={{ color: c.text }} />
          </Box>
        ) : (
          <Box sx={{ maxWidth: 480, mx: 'auto' }}>
            {field('LocationName', 'Location Name', !isNew)}
            {FIELDS.map(([k, label]) => field(k, label))}
            <Button
              fullWidth
              variant="contained"
              onClick={save}
              disabled={busy}
              sx={{ mt: 2, bgcolor: c.button, color: c.text, borderRadius: '12px', py: 1.25 }}
            >
              {isNew ? 'Add Location' : 'Save Changes'}
            </Button>
          </Box>
        )}
      </Box>

      {!isNew && (
        <CircleMenu
          items={[
            { icon: <WifiIcon />, title: 'Get WiFi For Locations', onClick: showWifi },
            { icon: <WifiFindSharpIcon />, title: 'Add WiFi to Location', onClick: () => setAddWifiOpen(true) },
          ]}
        />
      )}

      <Dialog open={!!wifiList} onClose={() => setWifiList(null)}
        PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>WiFi from API</DialogTitle>
        <DialogContent sx={{ maxHeight: 300 }}>
          <List dense>
            {(wifiList || []).map((w, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <ListItem key={`${w.WIFISSID}-${i}`}>
                <ListItemIcon><WifiIcon sx={{ color: '#2196F3' }} /></ListItemIcon>
                {/* WIFISSID / WIFIPASSWORD in caps — the endpoint's own column
                    names. WifiName/WifiPassword rendered blank rows. */}
                <ListItemText primary={w.WIFISSID ?? ''} secondary={`Password: ${w.WIFIPASSWORD ?? ''}`} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWifiList(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addWifiOpen} onClose={() => setAddWifiOpen(false)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '16px', minWidth: 300 } }}>
        <DialogTitle sx={{ color: c.text, fontSize: 20, textAlign: 'center' }}>
          Enter Your Wifi To Location
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth variant="standard" label="SSID" value={ssid}
            onChange={(e) => setSsid(e.target.value)}
            sx={{ '& .MuiInputBase-input': { color: c.text }, '& label': { color: c.text } }} />
          <Box sx={{ height: 16 }} />
          <TextField fullWidth variant="standard" label="Password" value={wifiPass}
            onChange={(e) => setWifiPass(e.target.value)}
            sx={{ '& .MuiInputBase-input': { color: c.text }, '& label': { color: c.text } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddWifiOpen(false)}
            sx={{ color: c.text, bgcolor: c.button }}>Skip</Button>
          <Button onClick={submitWifi} sx={{ color: c.text, bgcolor: c.button }}>Submit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
